import { useState, useMemo, useEffect } from 'react'
import { currentEncounterNumber } from '../../utils/caseEncounters'
import { caseDoctorId, caseDoctor, caseDoctorPhone } from '../../utils/caseDoctor'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Calendar, Clock, Phone, CheckCircle, MapPin, Video, Building, User, Shield, X, AlertCircle } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useI18n } from '../../i18n'
import { appLocale } from '../../utils/appLanguage'
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { formatDate, calendarMonthKey, calendarRangeLabel } from '../../utils/dateUtils'
import { formatAddress } from '../../utils/formatAddress'
import AppointmentCalendar from '../../components/AppointmentCalendar'
import { appointmentErrorKeyFor, appointmentRefused, classifySlotsResult, slotPlacesOf, slotPlace, slotPlaceNames } from '../../utils/bookableSlots'
import SlotPlaceLine from '../../components/SlotPlaceLine'
import { useBookingRecordingConsent } from '../../components/RecordingConsentSheet' // MEET PA0
import { resolveActiveEncounter, urgencyToTiming } from '../../hooks/useActiveCarePlan'
import {
  ENCOUNTER_CATEGORIES,
  categoryAllowsVideo,
  inferCategoryFromCase,
  normalizeCategory,
  API_CATEGORY_MAP,
} from '../../config/encounterConfig'
import { allowedVisitTypesForDoctor } from '../../utils/offeredModalities' // PROD-644
import { followUpForRebook } from '../../utils/declinedRequest' // PROD-1137
import { asText } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object
import { clinicianForms, clinicianFromText, clinicianName } from '../../utils/clinicianName.js'
import { safePath } from '../../utils/safePath' // PROD-1313: returnTo is checked before navigate()
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner
import { useHeroBox } from '../../hooks/useHeroBox' // PROD-1431 (RR1431 M1): the gradient box follows the real hero height

// PROD-1431 (R1431 M1): the booking hero is a pinned row 1 plus a body that scrolls away. HERO_H is the
// hero's height at rest with the synthetic fixture (160 px + the inset), the box both slices of the
// gradient are drawn in.
const HERO_GRAD = 'linear-gradient(135deg, #15354F 0%, #1E4A6E 100%)'
const HERO_ROW_H = 'calc(env(safe-area-inset-top, 0px) + 80px)'
const HERO_H = 'calc(env(safe-area-inset-top, 0px) + 160px)'
// RR1431 M1: HERO_H is only the first-paint fallback; useHeroBox writes the measured hero height to --hero-h.

// ★★★ v242.121.0 · FU-SCHEDULE-INLINE-1 — Dr. Rami: "For scheduling follow up can
//   we have it open on the same page - like we do when scheduling the initial
//   appointment?"
//   ★★★ THE INITIAL APPOINTMENT IS SCHEDULED INLINE inside ShareConversation, on the
//     page the patient is already on. The follow-up navigated to this route instead —
//     a full-screen context switch to pick a date.
//   ★★★ THIS SCREEN IS EMBEDDED RATHER THAN RE-BUILT, the same trick DoctorProfileScreen
//     already uses for the profile sheet. A second inline calendar would have been
//     quicker to write and would have become A SECOND BOOKING PATH: create-vs-update,
//     the reschedule branch, the external-doctor case and the no-online-scheduling
//     fallback all live in here, and a copy of the easy half is how two paths drift.
//   ★★ EMBEDDED MODE DROPS ITS OWN CONFIRMATION SCREEN ON PURPOSE. It reports the
//     booking to its host and the host re-renders — on Deliver that means the card
//     becomes the appointment card, which is a better confirmation than a page saying
//     "You're All Set" over the top of the plan.
function ScheduleFollowupScreen({
  embedded = false,
  patientRequestedProp,
  returnToProp,
  categoryProp,
  onScheduledProp,
} = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const [heroRow, heroBody] = useHeroBox() // PROD-1431 (RR1431 M1)
  const { currentCase, updateAndSaveCase } = useCaseStore()
  const { t, hasText, language } = useI18n()
  const appointmentErrorKey = (e) => appointmentErrorKeyFor(e, hasText) // PROD-1089: es withholds the physician_inactive sentence
  // MEET PA0: the booking-time recording question of a video visit (RecordingConsentSheet.jsx).
  const { askRecordingConsent, consentSheet } = useBookingRecordingConsent()
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isScheduled, setIsScheduled] = useState(false)
  
  // Check if this is a patient-initiated follow-up request
  const patientRequested = patientRequestedProp ?? location.state?.patientRequested ?? false
  // Where to navigate after scheduling (Deliver passes '/case/deliver')
  const returnTo = safePath(returnToProp || location.state?.returnTo, '/case/home')

  // v118: Encounter category — passed from DeliverScreen or inferred from case
  const categoryFromState = categoryProp ?? location.state?.category
  const category = categoryFromState 
    ? normalizeCategory(categoryFromState)
    : (patientRequested ? 'followUp' : inferCategoryFromCase(currentCase))
  const catConfig = ENCOUNTER_CATEGORIES[category] || ENCOUNTER_CATEGORIES.followUp
  // ★★ PROD-644 (patient P2): only the modalities the case's physician OFFERS for this visit kind
  //   (utils/offeredModalities, the md10a rule), with categoryAllowsVideo on top. When the doctor
  //   record does not say, the answer is today's: video by the category, in-person always.
  const offeredVisit = allowedVisitTypesForDoctor({ doctor: caseDoctor(currentCase), category, videoAllowedForCategory: categoryAllowsVideo(category) })
  const videoAllowed = offeredVisit.types.includes('video')
  const inPersonAllowed = offeredVisit.types.includes('in_person')

  // Category-aware title
  const categoryLabel = catConfig.label || 'Follow-up'

  // Scroll to top when component mounts or when scheduled confirmation appears
  useEffect(() => {
    // ★ embedded: this opens in place, part-way down the host's page. Scrolling that
    //   page to the top would throw away the reading position the patient was in.
    if (embedded) return
    window.scrollTo(0, 0)
    // Also scroll the app's scroll container (MobileLayout wrapper)
    try {
      // ★★★ v242.186.0 — AND THE FIX FOR IT BROKE EVERY CASE PAGE, WHICH IS THE WHOLE
      //   ENTRY. This was `require('../../App')` in an ESM bundle: undefined in the browser,
      //   thrown on every call, swallowed by its own catch, so the scroll reset had NEVER
      //   run. v242.185.0 "fixed" it with a static `import { scrollContainerRef } from
      //   '../../App'` — and App.jsx:76 imports THIS SCREEN. THAT IS A CYCLE, and in a
      //   bundle a cycle does not fail where you made it: App's module body stops halfway,
      //   and the next module to read one of its bindings gets `Cannot access 'x' before
      //   initialization`. It surfaced in CaseHome.jsx:529, three files away.
      //   ★★ THE `require` WAS DELIBERATELY LAZY AND I READ IT AS A TYPO. Whoever wrote it
      //   avoided exactly this. The scroll reset stays unbuilt rather than reintroduced
      //   wrongly a second time; MobileLayout owns that container and the honest fix is a
      //   prop or a context from the side that already has it, not a reach into App.
    } catch (e) {}
    // Fallback: find the scroll-wrapper div directly
    document.querySelector('.scroll-wrapper')?.scrollTo(0, 0)
  }, [isScheduled])

  // FHOOK: the null-case guard moved to the exported ScheduleFollowupGuard wrapper below, so this body
  //   (which owns every hook) is only ever mounted with a case. Rules of hooks: no early return before a hook.

  // Get follow-up info
  // ★ PROD-1137: after the physician cancelled the follow-up visit, the patient's choice here is a NEW
  //   booking with the same physician (followUpForRebook drops the cancelled slot and the cancel marks).
  const followUp = followUpForRebook(currentCase.followUp || currentCase.diagnose?.followUp || {})

  // v223.2.1/.2: Messenger-converted follow-up urgency.
  // When an MD converts a Messenger thread to a follow-up, the API stamps
  // care_plan.urgency ('asap' | 'within_week') + care_plan.windowDays (3 | 7)
  // on the active encounter — but NOT a free-text timeframe. Derive both the
  // header phrasing and the actual scheduling window from it.
  const activeEncounter = resolveActiveEncounter(currentCase)
  const activeCarePlan = activeEncounter?.care_plan || activeEncounter?.carePlan || {}
  const urgency = activeCarePlan.urgency || followUp.urgency || null
  const urgencyFromMessenger = !patientRequested && !!urgency
  const urgencyTiming = urgencyToTiming(urgency)
  const messengerWindowDays = activeCarePlan.windowDays || followUp.windowDays || urgencyTiming?.windowDays || null

  // v223.2.2: timeframe now respects messenger urgency ('3 days' / '1 week')
  // so the scheduling notes, badge, and no-slots copy all read correctly.
  const timeframe = patientRequested
    ? 'your convenience'
    : (asText(followUp.timeframe) || urgencyTiming?.timeframe || '2 weeks') // PROD-1156
  const isPreOp = followUp.isPreOp || false
  
  // Get MD's recommended visit type and default to it
  const recommendedVisitType = followUp.visitType || 'either' // 'virtual', 'in-person', 'either'
  const [visitType, setVisitType] = useState(() => {
    // If video not allowed for this category, force in-person
    if (!videoAllowed) return 'in-person'
    if (!inPersonAllowed) return 'telehealth' // PROD-644: a video-only physician
    if (recommendedVisitType === 'in-person') return 'in-person'
    if (recommendedVisitType === 'virtual') return 'telehealth'
    return ''
  })

  // Parse timeframe to get min and max days
  const getTimeframeRange = (tf) => {
    const lower = tf.toLowerCase()
    
    // Handle range formats like "3-5 days", "1-2 weeks", "4-6 weeks", "12-14 weeks", "6-7 months"
    const rangeMatch = lower.match(/(\d+)-(\d+)\s*(day|week|month|year)s?/)
    if (rangeMatch) {
      const minNum = parseInt(rangeMatch[1])
      const maxNum = parseInt(rangeMatch[2])
      const unit = rangeMatch[3]
      if (unit === 'day') return { minDays: minNum, maxDays: maxNum + 7 }
      if (unit === 'week') return { minDays: minNum * 7, maxDays: maxNum * 7 + 14 }
      if (unit === 'month') return { minDays: minNum * 30, maxDays: maxNum * 30 + 14 }
      if (unit === 'year') return { minDays: minNum * 365, maxDays: maxNum * 365 + 30 }
    }
    
    // Handle "over 1 year" format
    if (lower.includes('over') && lower.includes('year')) {
      return { minDays: 365, maxDays: 500 }
    }
    
    // Legacy formats (single value like "2 weeks")
    if (lower.includes('week')) {
      const num = parseInt(lower) || 2
      return { minDays: 1, maxDays: num * 7 + 14 }
    }
    if (lower.includes('month')) {
      const num = parseInt(lower) || 1
      return { minDays: 1, maxDays: num * 30 + 14 }
    }
    if (lower.includes('day')) {
      const num = parseInt(lower) || 14
      return { minDays: 1, maxDays: num + 14 }
    }
    if (lower.includes('year')) {
      return { minDays: 1, maxDays: 365 }
    }
    // Default to 3 months
    return { minDays: 1, maxDays: 90 }
  }

  // v223.2.2: For messenger-converted follow-ups the MD set an exact window
  // (asap → 3 days, within_week → 7 days). Honor it directly rather than running
  // it through getTimeframeRange, which pads every timeframe with a 2-week buffer.
  const { minDays, maxDays } = (urgencyFromMessenger && messengerWindowDays)
    ? { minDays: 0, maxDays: messengerWindowDays }
    : getTimeframeRange(timeframe)

  // v127.0: Fetch real doctor availability from API
  const [doctorSlots, setDoctorSlots] = useState(null)
  const [availabilityEnabled, setAvailabilityEnabled] = useState(null)
  const [slotsLoading, setSlotsLoading] = useState(true)
  const [slotDuration, setSlotDuration] = useState(30)
  // PROD-211: WHY there is nothing to pick, from bookableSlots' own classifySlotsResult
  // ('no-doctor' | 'scheduling-off' | 'no-hours' | 'none-open' | 'fetch-failed' | null).
  // slotsAttempt re-runs the read when the patient taps "Try again" after a failed one.
  const [slotsReason, setSlotsReason] = useState(null)
  const [slotsAttempt, setSlotsAttempt] = useState(0)
  // ★ setError WAS CALLED AND NEVER DECLARED (SETTER-UNBOUND-1). The 409 branch
  // below — the patient picked a slot somebody else had just taken — has called
  // setError(t('errors2.timeSlotBooked')) since v140.0, and there was no such
  // variable in this file, so the ONE path that exists to explain a double
  // booking crashed the screen instead. Nothing caught it: the name is
  // camelCase, and check-free-identifiers only reads SCREAMING_SNAKE.
  const [error, setError] = useState(null)

  // ★★★ v242.175.0 FU-SCHEDULE-DOCTORREF-1. This screen read `sentToDoctor` — the
  //     SHARE-FLOW column — and nothing else. On a follow-up the doctor reaches the case
  //     through the APPOINTMENT, so that column is empty on a case that plainly has a
  //     doctor: the patient was told their doctor has no online scheduling, and the same
  //     dead read withheld the office number on the very case that could not book.
  //     caseDoctor.js is now the one owner, so the id and the record cannot disagree.
  const doctorId = useMemo(() => caseDoctorId(currentCase), [currentCase])
  const doctorRecord = useMemo(() => caseDoctor(currentCase), [currentCase])
  const doctorPhone = useMemo(() => caseDoctorPhone(currentCase), [currentCase])
  // ★ the office block on the reinstated unavailable card, from the SAME record — the
  //   v242.123.0 original built four parallel fallback chains inline, one per field, and
  //   four chains over one question is how the id and the record come to disagree.
  const officeName = doctorRecord?.practice_name || ''
  const officeAddress = [formatAddress(doctorRecord?.practice_address), doctorRecord?.practice_city, doctorRecord?.practice_state]
    .filter(Boolean).join(', ')
  const doctorSurname = doctorRecord?.last_name
    || (currentCase?.sentToDoctorName || '').replace(/^Dr\.?\s+/i, '').split(' ').slice(-1)[0]
    || ''

  // ★ v242.176.0 — lifted verbatim out of the card it used to live inside, so the
  //   reinstated card can offer it as a text row rather than a tile. Fixed at
  //   v242.145.0 (VISITPREP-SCHEDULEFOLLOWUP-CALLSITE-1); unchanged here.
  const handleVisitPrep = async () => {
    try {
      // ★★★ v242.145.0 VISITPREP-SCHEDULEFOLLOWUP-CALLSITE-1.
      //     This passed `currentCase` POSITIONALLY into a function
      //     that destructures an options bag, so `currentCase`
      //     resolved to `currentCase.currentCase` — undefined —
      //     and the first property access threw. Every press since
      //     this button was added has produced the failure alert
      //     and never a document.
      const { generateVisitPrepPDF } = await import('../../utils/visitPrepPDF')
      const { casesAPI } = await import('../../services/api')
      const { useProfileStore, useUserStore } = await import('../../stores')
      const { facesheetReadRefused } = await import('../../utils/facesheet')
      let facesheet = null
      try {
        const res = await casesAPI.getFacesheet(currentCase?.id)
        facesheet = res?.facesheet || null
      } catch (err) {
        // ★ P2 Release 3 (P2-4): a 403 is the server refusing this case to this account.
        //   Every other failure falls back to the store below, as before.
        if (facesheetReadRefused(err)) {
          alert(t('visitPrep.facesheetDenied', "This case is not available to your account, so no document was created. If you switched accounts, sign in with the account this case belongs to."))
          return
        }
      }
      // ★ read imperatively: this is an event handler, not a render
      const _profile = useProfileStore.getState().profile
      const _user = useUserStore.getState().user
      await generateVisitPrepPDF({
        currentCase,
        facesheet,
        profile: _profile,
        shareCode: currentCase?.shareCode || '',
        qrDataUrl: null,
        externalDoctor: currentCase?.externalDoctor || currentCase?.external_doctor || null,
        preVisitSummary: currentCase?.preVisitSummary || currentCase?.pre_visit_summary,
        userPhone: _user?.phone || '',
        userEmail: _user?.email || '',
      })
    } catch (err) {
      console.error('[ScheduleFollowup] Visit prep generation failed:', err)
      alert(t('errors2.failedToGenerateVisitPrep'))
    }
  }


  useEffect(() => {
    const fetchSlots = async () => {
      if (!doctorId) {
        // The case genuinely names nobody — availability unavailable
        setSlotsReason('no-doctor')
        setAvailabilityEnabled(false)
        setSlotsLoading(false)
        return
      }
      try {
        const { doctorsAPI } = await import('../../services/api')
        // Request slots with buffer beyond maxDays, pass category for duration
        const apiCategory = API_CATEGORY_MAP[category] || category
        const result = await doctorsAPI.getAvailableSlots(doctorId, maxDays + 14, apiCategory)
        console.log('[ScheduleFollowup] Slots response:', result?.enabled, result?.slots?.length, 'slots')
        setDoctorSlots(result)
        // PROD-211: scheduling ON with no hours (enabled:true, reason:'no_hours') is
        // "online booking isn't available", the unavailable card below, and never the
        // "no openings in the window" card. A failed read is told apart from scheduling off.
        const { reason } = classifySlotsResult(result)
        setSlotsReason(reason)
        setAvailabilityEnabled((result?.enabled ?? false) && reason !== 'no-hours')
        if (result?.duration) setSlotDuration(result.duration)
      } catch (err) {
        console.warn('[ScheduleFollowup] Failed to fetch doctor slots:', err)
        setSlotsReason('fetch-failed')
        setAvailabilityEnabled(false)
      }
      setSlotsLoading(false)
    }
    fetchSlots()
  }, [doctorId, maxDays, category, slotsAttempt])

  // v127.0: Build available dates from API slots, filtered to MD's timeframe
  const availableDates = useMemo(() => {
    if (!doctorSlots?.slots?.length) return []
    const today = new Date()
    const minDate = new Date(today)
    minDate.setDate(today.getDate() + minDays)
    minDate.setHours(0, 0, 0, 0)       // v223.2.2: day-granular lower bound (asap allows today)
    const maxDate = new Date(today)
    maxDate.setDate(today.getDate() + maxDays)
    maxDate.setHours(23, 59, 59, 999)  // v223.2.2: include the full final day

    return doctorSlots.slots
      .filter(slot => {
        const d = new Date(slot.date + 'T00:00:00') // parse as local
        return d >= minDate && d <= maxDate
      })
      .map(slot => ({
        // PROD-1330: no Date object is carried. Every label is built from the date-only dateStr through
        // the calendar-day rule (dateUtils); a local-midnight Date formatted in LA showed the day before.
        dateStr: slot.date,
        times: slot.times || [],
        dayName: slot.dayName,
      }))
  }, [doctorSlots, minDays, maxDays])

  // Group dates by month for better display
  const datesByMonth = useMemo(() => {
    const grouped = {}
    availableDates.forEach(entry => {
      const monthKey = calendarMonthKey(entry.dateStr, 'en-US') // PROD-1330: the slot's own month
      if (!grouped[monthKey]) grouped[monthKey] = []
      grouped[monthKey].push(entry)
    })
    return grouped
  }, [availableDates])

  // v127.0: Time slots are now dynamic per selected date
  const timeSlotsForDate = useMemo(() => {
    if (!selectedDate || !availableDates.length) return []
    const entry = availableDates.find(d => d.dateStr === selectedDate)
    return (entry?.times || []).map(t => {
      const [h, m] = t.split(':').map(Number)
      const ampm = h >= 12 ? 'PM' : 'AM'
      const displayH = h > 12 ? h - 12 : (h === 0 ? 12 : h)
      return { display: `${displayH}:${String(m).padStart(2, '0')} ${ampm}`, value: t }
    })
  }, [selectedDate, availableDates])

  // ★ D7 (PROD-639, PD7-2): where the follow-up would be, from GET /slots' `locations` +
  //   `slotLocations` (utils/bookableSlots.js). null for an API without them, and for a
  //   telehealth visit: then nothing below renders and the screen is what it was before D7.
  const slotPlaces = useMemo(() => slotPlacesOf(doctorSlots), [doctorSlots])
  const pickedPlace = slotPlace(slotPlaces, selectedDate, selectedTime, visitType === 'telehealth')

  // Clear selected time when date changes (new date may not have the same slots)
  useEffect(() => {
    setSelectedTime('')
  }, [selectedDate])

  // Helper to get display time from stored 24-hour value
  const getDisplayTime = (value) => {
    if (!value) return value
    const [h, m] = value.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const displayH = h > 12 ? h - 12 : (h === 0 ? 12 : h)
    return `${displayH}:${String(m).padStart(2, '0')} ${ampm}`
  }

  const handleSchedule = async () => {
    if (!selectedDate || !selectedTime || !visitType) {
      alert('Please select date, time, and visit type')
      return
    }

    // Map visit type to appointment type
    const appointmentType = visitType === 'telehealth' ? 'video' : 'in_person'
    // MEET PA0: a NEW video visit's recording question comes BEFORE anything is sent; closing the
    //   sheet sends nothing (scripts/check-join-consent.mjs). A reschedule of an existing row is
    //   NOT asked: PUT /appointments/<id> does not record a decision (API M3), so an answer here
    //   would be dropped while an earlier one stands; the pre-join sheet (PA1) asks instead.
    const isNewBooking = !(followUp.appointmentScheduled && followUp.scheduledDate)
    const consent = isNewBooking
      ? await askRecordingConsent(appointmentType)
      : { proceed: true, fields: {} }
    if (!consent.proceed) return

    setIsSubmitting(true)
    setError(null)

    try {
      // Import the API
      const { appointmentsAPI, casesAPI } = await import('../../services/api')

      // Check if this is a reschedule (follow-up already exists)
      const existingFollowUp = followUp.appointmentScheduled && followUp.scheduledDate
      // Use real appointment ID from API if available, fall back to synthetic
      const existingAppointmentId = currentCase.followUpAppointment?.id || `followup-${currentCase.id}`
      
      let apiAppointment = null
      
      if (existingFollowUp) {
        // UPDATE existing appointment instead of creating new one
        console.log('[ScheduleFollowup] Rescheduling existing follow-up:', existingAppointmentId)
        try {
          const response = await appointmentsAPI.update(existingAppointmentId, {
            date: selectedDate,
            time: selectedTime,
            type: appointmentType,
            status: 'rescheduled',
            scheduled_by: 'patient'
          })
          // MEET PA4 (R5 info 5): not the returned row — a Meet row carries meeting_url.
          console.log('[ScheduleFollowup] Update response received, appointment:', !!response?.appointment)
          apiAppointment = response.appointment
        } catch (apiErr) {
          // P1 round 5 (M6): a refusal (doctor_has_no_hours, a slot conflict) is not "the
          //   appointment may not exist": creating the same slot after it is a second try
          //   at what was just refused. It goes to the outer catch, which shows it.
          if (appointmentRefused(apiErr)) throw apiErr
          console.warn('[ScheduleFollowup] Update API call failed, trying create:', apiErr)
          // Fallback to create if update fails (appointment may not exist)
          const appointmentData = {
            case_id: currentCase.id,
            date: selectedDate,
            time: selectedTime,
            type: appointmentType,
            appointment_type: 'follow-up',
            appointment_category: API_CATEGORY_MAP[category] || category,
            encounter_number: currentEncounterNumber(currentCase), // v135.2.0 PA-C1
            status: 'rescheduled',
            scheduled_by: 'patient',
            notes: t('scheduleFollowup.followUpWithin', { timeframe })
          }
          const response = await appointmentsAPI.create(appointmentData)
          apiAppointment = response.appointment
        }
      } else {
        // Create new appointment
        const appointmentData = {
          case_id: currentCase.id,
          date: selectedDate,
          time: selectedTime,
          type: appointmentType,
          appointment_type: 'follow-up',
          appointment_category: API_CATEGORY_MAP[category] || category,
          encounter_number: currentEncounterNumber(currentCase), // v135.2.0 PA-C1
          status: patientRequested ? 'pending' : 'scheduled',
          scheduled_by: 'patient',
          patient_requested: patientRequested,
          notes: patientRequested
            ? 'Patient-requested follow-up appointment'
            : `Follow-up appointment within ${timeframe}`,
          ...consent.fields, // MEET PA0: { recordingConsent, consentVersion } or {}
        }
        
        // MEET PA4 (R5 info 5): not the body — it carries the visit and the recording decision.
        console.log('[ScheduleFollowup] Creating new appointment via API, type:', appointmentType)
        try {
          const response = await appointmentsAPI.create(appointmentData)
          // MEET PA5: the id only — the returned row carries meeting_url on a Meet visit.
          console.log('[ScheduleFollowup] API response received, appointment id:', response?.appointment?.id ?? null)
          apiAppointment = response.appointment
        } catch (apiErr) {
          // P1 round 5 (M6) — the contract codes first: a 409 doctor_has_no_hours is not
          //   "this time slot was just booked", and clearing the picker for it is the
          //   pick-again loop the patient cannot leave. slot_conflict keeps the branch below.
          const refusalKey = appointmentErrorKey(apiErr)
          if (refusalKey && refusalKey !== 'errors2.timeSlotBooked') {
            setError(t(refusalKey))
            setIsSubmitting(false)
            return
          }
          // v140.0: Handle scheduling conflicts
          if (apiErr.status === 409 || apiErr.conflict) {
            setError(t('errors2.timeSlotBooked'))
            setSelectedDate(null)
            setSelectedTime(null)
            setIsSubmitting(false)
            return
          }
          console.warn('[ScheduleFollowup] API call failed, saving locally:', apiErr)
        }
      }
      
      // Create local appointment object with API response or local data
      const isReschedule = existingFollowUp
      const newAppointment = {
        id: apiAppointment?.id || existingAppointmentId,
        date: selectedDate,
        time: selectedTime,
        type: 'follow-up',
        visitType: visitType,
        typeLabel: visitType === 'telehealth' ? t('scheduleFollowup.telehealthFollowUp') : t('scheduleFollowup.inPersonFollowUp'),
        scheduledAt: new Date().toISOString(),
        scheduledBy: 'patient',
        status: isReschedule ? 'rescheduled' : (patientRequested ? 'pending' : 'scheduled'),
        doctorName: apiAppointment?.doctor_name || '',
        practiceName: apiAppointment?.practice_name || '',
        appointment_category: API_CATEGORY_MAP[category] || category,
        appointmentCategory: category,
        encounter_number: currentEncounterNumber(currentCase), // v135.2.0 PA-C1
        encounterNumber: currentEncounterNumber(currentCase),
      }
      
      // For rescheduling, replace the existing follow-up appointment, don't add new one
      let allAppointments = currentCase.appointments || []
      
      if (existingFollowUp) {
        // Remove old follow-up appointment and add updated one
        allAppointments = allAppointments.filter(a => 
          !a.id?.startsWith('followup-') && a.type !== 'follow-up'
        )
      }
      
      // Add the new/updated appointment
      allAppointments = [...allAppointments, newAppointment]
      
      // Update follow-up data
      const updatedFollowUp = {
        ...followUp,
        appointmentScheduled: true,
        scheduledDate: selectedDate,
        scheduledTime: selectedTime,
        visitType: visitType,
        scheduledBy: 'patient',
        scheduledByName: currentCase.patient?.name || currentCase.profile?.firstName || 'Patient',
        scheduledAt: existingFollowUp ? followUp.scheduledAt : new Date().toISOString(),
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'patient',
        appointmentStatus: isReschedule ? 'rescheduled' : (patientRequested ? 'pending' : 'scheduled'),
        patientRequested: patientRequested,
        requestedAt: patientRequested ? new Date().toISOString() : followUp.requestedAt
      }
      
      // Update local case and persist
      await updateAndSaveCase({
        status: patientRequested ? 'follow_up_requested' : 'scheduled',
        isShared: true,
        appointments: allAppointments,
        scheduledAppointment: newAppointment,
        followUp: updatedFollowUp,
        followUpAppointment: apiAppointment || newAppointment, // Store real appointment with ID for reschedule
        deliverState: 'scheduled',
      })
      
      // Sync follow-up data to API
      try {
        await casesAPI.update(currentCase.id, {
          status: patientRequested ? 'follow_up_requested' : 'scheduled',
          follow_up: updatedFollowUp
        })
        console.log('[ScheduleFollowup] Follow-up data synced to API with status:', patientRequested ? 'follow_up_requested' : 'scheduled')
      } catch (syncErr) {
        console.warn('[ScheduleFollowup] Failed to sync follow-up to API:', syncErr)
      }
      
      // v121.5.2: Removed encountersAPI.create for closed case reopen
      // The reopen bridge flow in DeliverScreen handles case conversion in-place
      // without creating new encounters. current_encounter stays at N+1.
      
      setIsScheduled(true)
      if (embedded) onScheduledProp?.()
    } catch (err) {
      console.error('Failed to schedule:', err)
      // P1 round 5 (M6): a refusal rethrown from the reschedule reads its own message.
      const refusalKey = appointmentErrorKey(err) || ((err?.status === 409 || err?.conflict === true) ? 'errors2.timeSlotBooked' : null)
      if (refusalKey) setError(t(refusalKey))
      else alert(t('scheduleFollowup.schedulingFailed', { error: err.message || t('common.unknownError') }))
    }
    
    setIsSubmitting(false)
  }

  if (isScheduled && !embedded) {
    return (
      <div className="screen-min-h bg-gray-50">
        {/* Header — Navy */}
        <div className="text-white px-4 py-6 pb-8" style={{ background: 'linear-gradient(135deg, #15354F 0%, #1E4A6E 100%)', paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(returnTo)} className="p-2 -ml-2 rounded-full hover:bg-white/10">
              <ArrowLeft size={24} />
            </button>
            <h1 className="text-xl font-bold">{t('scheduleFollowup.appointmentScheduled')}</h1>
          </div>
        </div>

        <div className="px-4 -mt-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            {/* Green success — semantic, stays green */}
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#15354F' }}>
              {patientRequested ? t('scheduleFollowup.requestSubmitted') : 'You\'re All Set!'}
            </h2>
            <p className="text-gray-600 mb-6">
              {patientRequested 
                ? t('scheduleFollowup.requestSentToDoctor')
                : t('scheduleFollowup.appointmentScheduled')}
            </p>
            
            {/* Appointment details — Ice Blue bg, Blue icons */}
            <div className="rounded-xl p-4 mb-6 text-left" style={{ backgroundColor: '#E9F8FF' }}>
              <div className="flex items-center gap-3 mb-3">
                <Calendar style={{ color: '#46A8F9' }} size={20} />
                <span className="font-medium" style={{ color: '#15354F' }}>
                  {formatDate(selectedDate, { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
              <div className="flex items-center gap-3 mb-3">
                <Clock style={{ color: '#46A8F9' }} size={20} />
                <span className="font-medium" style={{ color: '#15354F' }}>{getDisplayTime(selectedTime)} PT · {slotDuration} min</span>
              </div>
              <div className="flex items-center gap-3">
                {visitType === 'telehealth' ? (
                  <>
                    <Video style={{ color: '#46A8F9' }} size={20} />
                    <span className="font-medium" style={{ color: '#15354F' }}>{t('scheduleFollowup.telehealthVisit')}</span>
                  </>
                ) : (
                  <>
                    <Building style={{ color: '#46A8F9' }} size={20} />
                    <span className="font-medium" style={{ color: '#15354F' }}>{t('scheduleFollowup.inpersonVisit')}</span>
                  </>
                )}
              </div>
              <SlotPlaceLine place={pickedPlace} className="mt-3 ml-[32px]" />
            </div>

            <p className="text-sm text-gray-500 mb-6">
              {patientRequested 
                ? 'Your doctor will review and confirm the appointment. You\'ll be notified once confirmed.'
                : 'You\'ll receive a reminder before your appointment.'}
            </p>

            {/* Blue CTA */}
            <button
              onClick={() => navigate(returnTo)}
              className="w-full py-3 text-white rounded-full font-semibold"
              style={{ backgroundColor: '#46A8F9' }}
            >
              {returnTo === '/case/deliver' ? t('scheduleFollowup.continueToDeliver') : 'Back to Case Home'}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={embedded ? '' : 'screen-min-h bg-gray-50'}>
      {!embedded && (
      /* Header — Navy. PROD-1431 (R1431 M1): only row 1 (back, title, Pre-Op badge) is pinned at the
         top; its navy top padding is the status-bar band. The body below it scrolls away and stays
         unpositioned, so the doctor card's -mt-4 overlap paints over it as before. Both paint slices
         of one gradient box, as tall as the measured hero (useHeroBox), so at rest the hero reads as one piece. */
      <>
      <div className="sticky-inset-header text-white px-4 flex-shrink-0 overflow-hidden" ref={heroRow} style={{ background: `${HERO_GRAD} 0 0 / 100% var(--hero-h, ${HERO_H}) no-repeat`, height: HERO_ROW_H, paddingTop: 'calc(1.5rem + env(safe-area-inset-top, 0px))' }}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-white/10">
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl font-bold truncate min-w-0">
              {patientRequested ? t('scheduleFollowup.requestCategory', { category: categoryLabel }) : t('scheduleFollowup.scheduleCategory', { category: categoryLabel })}
            </h1>
            {isPreOp && (
              <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full font-medium">
                {t('scheduleFollowup.preOp', 'Pre-Op')}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="text-white px-4 pb-8" ref={heroBody} style={{ background: `${HERO_GRAD} 0 calc(-1 * ${HERO_ROW_H}) / 100% var(--hero-h, ${HERO_H}) no-repeat, #1E4A6E` }}>
        <p style={{ color: 'rgba(167,217,252,0.8)' }}>
          {patientRequested
            ? t('scheduleFollowup.requestFollowUpMsg')
            : urgencyFromMessenger
              ? <>Your doctor has asked you to schedule a follow-up <strong className="text-white">{urgency === 'asap' ? 'as soon as possible' : 'within a week'}</strong></>
              : <>Your doctor recommended a {isPreOp ? 'pre-operative ' : ''}follow-up within <strong className="text-white">{timeframe}</strong></>}
        </p>
        {/* Timeframe date range badge */}
        {!patientRequested && !slotsLoading && availableDates.length > 0 && (() => {
          // PROD-1330: "Nov 3 – Dec 1, 2026" from the date-only slot strings (calendar-day rule).
          const rangeLabel = calendarRangeLabel(availableDates[0].dateStr, availableDates[availableDates.length - 1].dateStr, appLocale(language))
          return (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(70,168,249,0.2)', border: '1px solid rgba(70,168,249,0.3)' }}>
              <Clock size={14} style={{ color: '#A7D9FC' }} />
              <span className="text-[12px] font-semibold" style={{ color: '#A7D9FC' }}>
                {rangeLabel ? `${rangeLabel} · ` : ''}{slotDuration} min slots
              </span>
            </div>
          )
        })()}
      </div>
      </>
      )}

      <div className={embedded ? 'pb-2' : 'px-4 -mt-4 pb-8'}>
        {/* Doctor Info Card — ★ embedded: the host already shows the doctor row
            directly above this, and the same face twice is not reassurance. */}
        {!embedded && doctorRecord && (() => {
          const doc = doctorRecord
          return (
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#E9F8FF] flex items-center justify-center overflow-hidden flex-shrink-0">
                  {doc.profile_photo ? (
                    <img src={doc.profile_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[#46A8F9] font-bold text-base">{doc.first_name?.[0]}{doc.last_name?.[0]}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[15px] font-bold text-[#15354F]">{clinicianName(doc)}</h3>
                  <p className="text-[13px] text-[#46A8F9] font-medium">{formatProviderSpecialty(doc.specialty || doc.specialties?.[0], t)}</p>
                </div>
                {doc.board_certified && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-green-50 rounded-lg">
                    <Shield size={12} className="text-green-600" />
                    <span className="text-[10px] font-semibold text-green-700">{t('scheduleFollowup.boardCertified')}</span>
                  </div>
                )}
              </div>
              {(doc.practice_name || doc.practice_address) && (
                <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5">
                  {doc.practice_name && (
                    <div className="flex items-center gap-2 text-[12px] text-gray-600">
                      <Building size={13} className="text-gray-400 flex-shrink-0" />
                      {doc.practice_name}
                    </div>
                  )}
                  {doc.practice_address && (
                    <div className="flex items-start gap-2 text-[12px] text-gray-500">
                      <MapPin size={13} className="text-gray-400 flex-shrink-0 mt-0.5" />
                      {/* v222.4.8 — defensive: practice_address may be a JSONB dict / JSON-string */}
                      <div>{formatAddress(doc.practice_address)}{doc.practice_city && <>, {doc.practice_city}{doc.practice_state && `, ${doc.practice_state}`} {doc.practice_zip}</>}</div>
                    </div>
                  )}
                  {/* ★ v242.175.0 — this read `doc.phone` ALONE, and an appointment-sourced
                      doctor carries the number as practice_phone: the office row rendered
                      no phone at all on exactly the follow-up case that could not book. */}
                  {doctorPhone && (
                    <div className="flex items-center gap-2 text-[12px]">
                      <Phone size={13} className="text-gray-400 flex-shrink-0" />
                      <a href={`tel:${doctorPhone}`} className="text-[#46A8F9] font-medium">{doctorPhone}</a>
                    </div>
                  )}
                </div>
              )}
              <button
                onClick={() => navigate(`/doctor/${doc.id}`, { state: { mode: 'view' } })}
                className="flex items-center gap-1.5 text-[12px] font-semibold text-[#46A8F9] mt-3 pt-2.5 border-t border-gray-100"
              >
                <User size={13} /> {t('scheduleFollowup.viewFullProfile', 'View Full Profile')}
              </button>
            </div>
          )
        })()}

        {/* v127.0: Loading State */}
        {slotsLoading && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center mb-6">
            <OrellaSpinner size={32} className="mx-auto mb-3" />
            <p className="text-sm text-gray-500">{t('scheduleFollowup.loadingDoctorsAvailability')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('scheduleFollowup.pacificTimePt')}</p>
          </div>
        )}

        {/* ★★★ v242.176.0 · FU-UNAVAILABLE-CARD-1 — BUILD123-REINSTATE-1 (3 of 3).
            Reinstated from v242.123.0, withdrawn whole during the DELIVER-AGENT-OPENER-2
            revert chain for a defect in a different file. Mocked and agreed before any
            code: claude/orella_deliver_scheduling_mockup_v1.html.
            ★★★ THIS STATE USED TO BE A FULL PAGE — a 56px hero icon, a Visit Prep button
              and "Back to Case". Inline, on the page it is now part of, every one of those
              is wrong: the patient IS on the case, and a hero icon inside a list of 36px
              tiles is the loudest thing on a screen delivering the least.
            ★★ AMBER, because this is the one card on the screen waiting on the patient to
              do something a tap cannot do.
            ★★★ AND IT CAN ACT: the number comes from utils/caseDoctor.js, not from
              sentToDoctorData — the record that is missing precisely when this card
              appears. WITH NO NUMBER ANYWHERE IT DRAWS NO BUTTON; a dead tel: link is the
              affordance deleted from this screen at TF b40.
            ★★★ VISIT PREP SURVIVES, AND THAT IS A DEPARTURE FROM THE 2026-08-22 DESIGN. It
              was dropped then on his call — "would have to be built so I prefer to leave it
              out for now" — and v242.145.0 BUILT IT: the call site had passed `currentCase`
              positionally into a function that destructures an options bag, so every press
              since the button was added produced the failure alert and never a document.
              The fact the ruling rested on has changed, so it stays — as a quiet text row,
              not a tile, because the call is still the thing to do. */}
        {!slotsLoading && availabilityEnabled === false && (
          <div className="bg-white rounded-2xl border border-[#15354F] p-[13px] pb-[11px] mb-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#FFFBEB' }}>
                <Calendar size={16} style={{ color: '#B45309' }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[14.5px] font-bold leading-tight" style={{ color: '#15354F' }}>
                  {slotsReason === 'fetch-failed' ? t('caseHome.slotsFetchFailed')
                    : slotsReason === 'no-hours' ? t('appointment.noOnlineBookingTitle')
                    : t('scheduleFollowup.unavailableApptTitle', { category: categoryLabel })}
                </p>
                {timeframe && (
                  <p className="text-[11px] text-slate-500 mt-0.5">{t('scheduleFollowup.recommendedWithin', { timeframe })}</p>
                )}
              </div>
              {slotsReason !== 'fetch-failed' && (
              <span className="flex-none text-[9.5px] font-extrabold uppercase tracking-wide px-[11px] py-1 rounded-full border"
                style={{ backgroundColor: '#FFFBEB', color: '#B45309', borderColor: '#FDE68A' }}>
                {doctorPhone ? t('scheduleFollowup.unavailablePillCall') : t('scheduleFollowup.unavailablePillArrange')}
              </span>
              )}
            </div>

            {/* PROD-211: a failed read is not a booking policy. No "call to book" pill or
                sentence; the patient can read the calendar again instead. */}
            {slotsReason === 'fetch-failed' ? (
              <button
                onClick={() => { setSlotsLoading(true); setSlotsAttempt((n) => n + 1) }}
                className="mt-2.5 w-full py-2.5 rounded-[14px] border-[1.5px] border-[#46A8F9] text-[13px] font-bold bg-white"
                style={{ color: '#46A8F9' }}
              >
                {t('common.tryAgain')}
              </button>
            ) : (
            <div className="mt-2.5 rounded-[10px] px-3 py-2.5 flex gap-2 items-start"
              style={{ backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}>
              <AlertCircle size={14} style={{ color: '#B45309' }} className="flex-shrink-0 mt-[2px]" />
              <p className="text-[12px] leading-relaxed" style={{ color: '#7C4A0C' }}>
                {t(doctorPhone ? 'scheduleFollowup.unavailableWithPhone' : 'scheduleFollowup.unavailableNoPhone', {
                  office: doctorSurname
                    ? t('scheduleFollowup.drOffice', { ...clinicianForms(doctorRecord?.last_name ? doctorRecord : clinicianFromText(doctorSurname), 'short'), name: doctorSurname })
                    : t('scheduleFollowup.thisOffice'),
                })}
              </p>
            </div>
            )}

            {(officeName || officeAddress) && (
              <div className="mt-2 rounded-[10px] px-3 py-2.5 flex gap-2 items-start" style={{ backgroundColor: '#E9F8FF' }}>
                <MapPin size={14} style={{ color: '#46A8F9' }} className="flex-shrink-0 mt-[2px]" />
                <div className="min-w-0">
                  {officeName && <p className="text-[12px] font-semibold" style={{ color: '#15354F' }}>{officeName}</p>}
                  {officeAddress && <p className="text-[11px] text-slate-500 mt-0.5">{officeAddress}</p>}
                </div>
              </div>
            )}

            {doctorPhone && (
              <>
                <a href={`tel:${doctorPhone}`}
                  className="mt-2.5 w-full flex items-center justify-center gap-2 py-3 rounded-[14px] text-white text-[14px] font-bold no-underline"
                  style={{ backgroundColor: '#46A8F9' }}>
                  <Phone size={16} /> {t('scheduleFollowup.callNumber', { phone: doctorPhone })}
                </a>
                <p className="mt-2 pt-2.5 border-t border-slate-100 text-[11px] text-slate-500">
                  {doctorSurname
                    ? t('scheduleFollowup.officeNumberOnRecordFor', { ...clinicianForms(doctorRecord?.last_name ? doctorRecord : clinicianFromText(doctorSurname), 'short'), name: doctorSurname })
                    : t('scheduleFollowup.officeNumberOnRecord')}
                </p>
              </>
            )}

            <button
              onClick={handleVisitPrep}
              className="w-full mt-2 py-2 text-[12px] font-semibold text-left bg-transparent border-0"
              style={{ color: '#46A8F9' }}
            >
              {t('scheduleFollowup.downloadVisitPrep')}
            </button>

            {!embedded && (
              <button
                onClick={() => navigate(returnTo)}
                className="w-full mt-3 py-3 rounded-full border-[1.5px] border-gray-200 text-gray-500 font-semibold text-[14px]"
              >
                {t('scheduleFollowup.backToCase')}
              </button>
            )}
          </div>
        )}

        {/* v127.0: Availability enabled but no slots in timeframe */}
        {!slotsLoading && availabilityEnabled && availableDates.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center mb-6">
            <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-700 mb-1">{t('scheduleFollowup.noAvailableSlots')}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('scheduleFollowup.noOpeningsInWindow', { defaultValue: 'Your doctor doesn\u2019t have openings within the recommended {{window}} window.' })
                .split('{{window}}')
                .flatMap((part, i) => i === 0 ? [part] : [<strong key={`w${i}`}>{timeframe}</strong>, part])}
            </p>
            {doctorPhone && (
              <a 
                href={`tel:${doctorPhone}`}
                className="inline-flex items-center gap-2 font-medium" style={{ color: '#46A8F9' }}
              >
                <Phone size={16} />
                {t('scheduleFollowup.callOfficeToSchedule', 'Call Office to Schedule')}
              </a>
            )}
          </div>
        )}

        {/* Visit Type + Date + Time Selection — only when slots are available */}
        {!slotsLoading && availabilityEnabled && availableDates.length > 0 && (
          <>
        {/* Visit Type Selection */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold" style={{ color: '#15354F' }}>{t('scheduleFollowup.visitType')}</h2>
            {recommendedVisitType && recommendedVisitType !== 'either' && (
              <span className="text-xs font-medium" style={{ color: '#46A8F9' }}>
                {t('deliver.doctorRecommends', 'Doctor recommends:')} {recommendedVisitType === 'in-person' ? 'In-Person' : 'Virtual'}
              </span>
            )}
          </div>
          <div className={`grid ${inPersonAllowed ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
            {inPersonAllowed && (
            <button
              onClick={() => setVisitType('in-person')}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 relative ${
                visitType === 'in-person'
                  ? 'border-[#46A8F9] bg-[#E9F8FF]'
                  : 'border-gray-200 hover:border-[#A7D9FC]'
              }`}
            >
              {recommendedVisitType === 'in-person' && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#46A8F9' }}>
                  <CheckCircle className="text-white" size={12} />
                </span>
              )}
              <Building className={`${visitType === 'in-person' ? 'text-[#46A8F9]' : 'text-gray-400'}`} size={28} />
              <span className={`font-medium ${visitType === 'in-person' ? 'text-[#15354F]' : 'text-gray-600'}`}>
                {t('caseHome.inPerson')}
              </span>
            </button>
            )}
            <button
              onClick={() => videoAllowed && setVisitType('telehealth')}
              disabled={!videoAllowed}
              className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 relative ${
                !videoAllowed
                  ? 'opacity-40 cursor-not-allowed border-gray-200 bg-gray-50'
                  : visitType === 'telehealth'
                    ? 'border-[#46A8F9] bg-[#E9F8FF]'
                    : 'border-gray-200 hover:border-[#A7D9FC]'
              }`}
            >
              {recommendedVisitType === 'virtual' && videoAllowed && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#46A8F9' }}>
                  <CheckCircle className="text-white" size={12} />
                </span>
              )}
              {!videoAllowed && (
                <span className="absolute -top-2 -right-2 flex items-center gap-0.5 text-[8px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full border border-amber-200">
                  <AlertCircle size={8} />
                  {t('scheduleFollowup.inPersonOnly')}
                </span>
              )}
              <Video className={`${!videoAllowed ? 'text-gray-300' : visitType === 'telehealth' ? 'text-[#46A8F9]' : 'text-gray-400'}`} size={28} />
              <span className={`font-medium ${!videoAllowed ? 'text-gray-400' : visitType === 'telehealth' ? 'text-[#15354F]' : 'text-gray-600'}`}>
                {t('appointment.telehealth', 'Telehealth')}
              </span>
            </button>
          </div>
        </div>

        {/* Date & Time Selection — Calendar */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold" style={{ color: '#15354F' }}>{t('scheduleFollowup.selectDateTime')}</h2>
            <span className="text-[11px] text-gray-400 font-medium">{t('scheduleFollowup.pacificTime')}</span>
          </div>
          <AppointmentCalendar
            availableDates={availableDates.map(d => d.dateStr)}
            timeSlots={timeSlotsForDate.map(s => s.value)}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            onSelectDate={setSelectedDate}
            onSelectTime={setSelectedTime}
            timeSubLabels={slotPlaceNames(slotPlaces, selectedDate, timeSlotsForDate.map(s => s.value), visitType === 'telehealth')}
          />
          <SlotPlaceLine place={selectedDate && selectedTime ? pickedPlace : null} className="mt-3" />
        </div>

        {/* Schedule Button — Blue */}
        <button
          onClick={handleSchedule}
          disabled={!selectedDate || !selectedTime || !visitType || isSubmitting}
          className="w-full py-4 text-white rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          style={{ backgroundColor: '#46A8F9' }}
        >
          {isSubmitting ? (
            <>
              <OrellaSpinner size={20} tone="inherit" />
              {patientRequested ? t('scheduleFollowup.submittingRequest') : t('scheduleFollowup.scheduling')}
            </>
          ) : (
            <>
              <Calendar size={20} />
              {patientRequested ? t('scheduleFollowup.requestAppointment') : t('scheduleFollowup.scheduleAppointment')}
            </>
          )}
        </button>

        {error && (
          <div className="mt-4 rounded-xl border px-4 py-3 text-sm"
               style={{ borderColor: '#FCA5A5', background: '#FEF2F2', color: '#B91C1C' }}>
            {error}
          </div>
        )}

        {/* TF b40 — the "Need a different time?" / call-the-office block that
            sat here was removed at Dr. Rami's call. It dialled
            sentToDoctorData.phone || practice_phone, and BOTH are routinely
            absent on a follow-up case, so the affordance rendered a live tel:
            link with an empty number. The grid above already offers every open
            slot; a phone number is not the fallback for a full day. */}
          </>
        )}
      </div>

      {consentSheet}
    </div>
  )
}

// FHOOK: outer guard component. Same output as the old in-body guard: no case -> (not embedded)
// bounce to /home and render nothing; with a case -> the body, same props.
export default function ScheduleFollowupGuard(props = {}) {
  const navigate = useNavigate()
  const { currentCase } = useCaseStore()
  if (!currentCase) {
    // ★ embedded: the host owns the route. Bouncing it would navigate a page the
    //   patient did not ask to leave.
    if (!props.embedded) navigate('/home')
    return null
  }
  return <ScheduleFollowupScreen {...props} />
}
